import type { IETMFRepository } from "@/repositories/ETMFRepository";
import {
  assertUploadWithin10Mb,
  uploadSizeLimitApiMessage,
} from "@/constants/uploadLimits";
import { assertESignatureCeremonyPayload } from "@/utils/buildAttestationESignature";
import { getErrorMessage } from "@/utils/errorMessage";
import type {
  ETMFDocumentDto,
  ETMFDocumentsQuery,
  ETMFUploadPayload,
  ETMFUploadResponse,
  ETMFQCReviewItem,
  ETMFCompletenessResponse,
  ETMFRetentionPolicyDto,
  ETMFPaginatedData,
  ETMFDocumentUpdatePayload,
  ETMFQCCompletePayload,
  ETMFArtifactTypeDto,
  ETMFDocumentVersionDto,
  ETMFGovernanceContract,
  ETMFGovernedArtifact,
  ETMFTmfModelDto,
  ETMFTmfModelVersionDto,
  CreateTmfModelPayload,
  CreateTmfModelVersionPayload,
  GovernanceReasonPayload,
  ETMFGovernanceImpact,
  ETMFLegacyMappingDto,
  UpsertLegacyMappingPayload,
  ETMFChangeRequestDto,
  ETMFGovernanceCatalog,
} from "@/types/etmf/etmf.type";

export interface IETMFUseCase {
  getDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>>;
  getDocument(id: string): Promise<ETMFDocumentDto | null>;
  getDocumentDownloadUrl(
    id: string,
  ): Promise<{ url: string; fileName: string }>;
  getDocumentStats(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
  }): Promise<Record<string, unknown>>;
  uploadDocument(payload: ETMFUploadPayload): Promise<ETMFUploadResponse>;
  updateDocument(
    id: string,
    payload: ETMFDocumentUpdatePayload,
  ): Promise<ETMFDocumentDto | null>;
  deleteDocument(id: string): Promise<void>;
  archiveDocument(id: string): Promise<void>;
  restoreDocument(id: string): Promise<void>;
  uploadNewVersion(
    id: string,
    file: File,
    version_notes?: string,
  ): Promise<ETMFDocumentDto | null>;
  getDocumentVersions(id: string): Promise<ETMFDocumentVersionDto[]>;
  getQCPendingReviews(query?: {
    page?: number;
    limit?: number;
    status?: string;
    study_id?: string;
    site_id?: string;
  }): Promise<ETMFPaginatedData<ETMFQCReviewItem>>;
  getQCReview(id: string): Promise<ETMFQCReviewItem | null>;
  getReviewByDocument(documentId: string): Promise<ETMFQCReviewItem | null>;
  completeQCReview(
    id: string,
    payload: ETMFQCCompletePayload,
  ): Promise<ETMFQCReviewItem | null>;
  getCompleteness(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
    group_by?: "study" | "site" | "zone" | "study-site";
    use_qc_checklist?: boolean;
  }): Promise<ETMFCompletenessResponse>;
  getRetentionPolicies(query?: {
    artifact_type?: string;
    status?: "Active" | "Expired" | "ACTIVE" | "EXPIRED";
  }): Promise<ETMFRetentionPolicyDto[]>;
  createRetentionPolicy(body: {
    artifactType: string;
    retentionPeriodYears: number;
  }): Promise<ETMFRetentionPolicyDto>;
  updateRetentionPolicy(
    id: string,
    body: { retentionPeriodYears?: number; status?: "ACTIVE" | "EXPIRED" },
  ): Promise<ETMFRetentionPolicyDto>;
  getArtifactTypes(query?: {
    zone?: string;
    required?: boolean;
  }): Promise<ETMFArtifactTypeDto[]>;
  getInspectionDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>>;
  exportTmSnapshot(studyId: string): Promise<Blob>;
  getGovernanceContract(query?: {
    study_id?: string;
    site_id?: string;
    country_code?: string;
  }): Promise<ETMFGovernanceContract>;
  getEligibleArtifacts(query: {
    study_id: string;
    site_id?: string;
    country_code?: string;
  }): Promise<{
    items: ETMFGovernedArtifact[];
    governed: boolean;
    model_version_id?: string;
  }>;
  getGovernanceCatalog(query?: {
    model_id?: string;
    version_id?: string;
  }): Promise<ETMFGovernanceCatalog>;
  listGovernanceModels(): Promise<{ items: ETMFTmfModelDto[] }>;
  createGovernanceModel(body: CreateTmfModelPayload): Promise<ETMFTmfModelDto>;
  listGovernanceVersions(
    modelId: string,
  ): Promise<{ items: ETMFTmfModelVersionDto[] }>;
  createGovernanceVersion(
    modelId: string,
    body: CreateTmfModelVersionPayload,
  ): Promise<ETMFTmfModelVersionDto>;
  submitGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto>;
  approveGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto>;
  previewGovernanceImpact(versionId: string): Promise<ETMFGovernanceImpact>;
  assignStudyModel(
    studyId: string,
    body: { model_version_id: string; reason: string; row_version?: number },
  ): Promise<Record<string, unknown>>;
  listLegacyMappings(query?: {
    study_id?: string;
  }): Promise<{ items: ETMFLegacyMappingDto[] }>;
  upsertLegacyMapping(
    body: UpsertLegacyMappingPayload,
  ): Promise<ETMFLegacyMappingDto>;
  listChangeRequests(query: {
    study_id: string;
  }): Promise<{ items: ETMFChangeRequestDto[] }>;
  createChangeRequest(body: {
    study_id: string;
    reason: string;
  }): Promise<ETMFChangeRequestDto>;
}

export class ETMFUseCase implements IETMFUseCase {
  constructor(private readonly repository: IETMFRepository) {}

  async getDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>> {
    try {
      return await this.repository.getDocuments(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch eTMF documents",
      );
    }
  }

  async getDocument(id: string): Promise<ETMFDocumentDto | null> {
    try {
      return await this.repository.getDocument(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to fetch document",
      );
    }
  }

  async getDocumentDownloadUrl(
    id: string,
  ): Promise<{ url: string; fileName: string }> {
    try {
      return await this.repository.getDocumentDownloadUrl(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to get download URL",
      );
    }
  }

  async getDocumentStats(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
  }): Promise<Record<string, unknown>> {
    try {
      return await this.repository.getDocumentStats(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch document stats",
      );
    }
  }

  async uploadDocument(
    payload: ETMFUploadPayload,
  ): Promise<ETMFUploadResponse> {
    try {
      if (!payload.file) {
        throw new Error("File is required");
      }
      if (!payload.study_id || !payload.zone || !payload.artifact_type) {
        throw new Error("Missing required metadata fields");
      }
      assertUploadWithin10Mb(payload.file);

      return await this.repository.uploadDocument(payload);
    } catch (error) {
      const sizeMessage = uploadSizeLimitApiMessage(error);
      throw new Error(
        sizeMessage ?? getErrorMessage(error, "Failed to upload document"),
      );
    }
  }

  async updateDocument(
    id: string,
    payload: ETMFDocumentUpdatePayload,
  ): Promise<ETMFDocumentDto | null> {
    try {
      return await this.repository.updateDocument(id, payload);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to update document",
      );
    }
  }

  async deleteDocument(id: string): Promise<void> {
    try {
      await this.repository.deleteDocument(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to delete document",
      );
    }
  }

  async archiveDocument(id: string): Promise<void> {
    try {
      await this.repository.archiveDocument(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to archive document",
      );
    }
  }

  async restoreDocument(id: string): Promise<void> {
    try {
      await this.repository.restoreDocument(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to restore document",
      );
    }
  }

  async uploadNewVersion(
    id: string,
    file: File,
    version_notes?: string,
  ): Promise<ETMFDocumentDto | null> {
    try {
      return await this.repository.uploadNewVersion(id, file, version_notes);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to upload new version",
      );
    }
  }

  async getDocumentVersions(id: string): Promise<ETMFDocumentVersionDto[]> {
    try {
      return await this.repository.getDocumentVersions(id);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch document versions",
      );
    }
  }

  async getQCPendingReviews(query?: {
    page?: number;
    limit?: number;
    status?: string;
    study_id?: string;
    site_id?: string;
  }): Promise<ETMFPaginatedData<ETMFQCReviewItem>> {
    try {
      return await this.repository.getQCPendingReviews(query);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to fetch QC reviews",
      );
    }
  }

  async getQCReview(id: string): Promise<ETMFQCReviewItem | null> {
    try {
      return await this.repository.getQCReview(id);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to fetch QC review",
      );
    }
  }

  async getReviewByDocument(
    documentId: string,
  ): Promise<ETMFQCReviewItem | null> {
    try {
      return await this.repository.getReviewByDocument(documentId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to get QC review for document",
      );
    }
  }

  async completeQCReview(
    id: string,
    payload: ETMFQCCompletePayload,
  ): Promise<ETMFQCReviewItem | null> {
    try {
      return await this.repository.completeQCReview(id, payload);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to complete QC review",
      );
    }
  }

  async getCompleteness(query?: {
    study_id?: string;
    site_id?: string;
    zone?: string;
    group_by?: "study" | "site" | "zone" | "study-site";
    use_qc_checklist?: boolean;
  }): Promise<ETMFCompletenessResponse> {
    try {
      return await this.repository.getCompleteness(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch completeness data",
      );
    }
  }

  async getRetentionPolicies(query?: {
    artifact_type?: string;
    status?: "Active" | "Expired" | "ACTIVE" | "EXPIRED";
  }): Promise<ETMFRetentionPolicyDto[]> {
    try {
      return await this.repository.getRetentionPolicies(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch retention policies",
      );
    }
  }

  async createRetentionPolicy(body: {
    artifactType: string;
    retentionPeriodYears: number;
  }): Promise<ETMFRetentionPolicyDto> {
    try {
      return await this.repository.createRetentionPolicy(body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create retention policy",
      );
    }
  }

  async updateRetentionPolicy(
    id: string,
    body: { retentionPeriodYears?: number; status?: "ACTIVE" | "EXPIRED" },
  ): Promise<ETMFRetentionPolicyDto> {
    try {
      return await this.repository.updateRetentionPolicy(id, body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to update retention policy",
      );
    }
  }

  async getArtifactTypes(query?: {
    zone?: string;
    required?: boolean;
  }): Promise<ETMFArtifactTypeDto[]> {
    try {
      return await this.repository.getArtifactTypes(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch artifact types",
      );
    }
  }

  async getInspectionDocuments(
    query?: ETMFDocumentsQuery,
  ): Promise<ETMFPaginatedData<ETMFDocumentDto>> {
    try {
      return await this.repository.getInspectionDocuments(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch inspection documents",
      );
    }
  }

  async exportTmSnapshot(studyId: string): Promise<Blob> {
    try {
      return await this.repository.exportTmSnapshot(studyId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to export TMF snapshot",
      );
    }
  }

  async getGovernanceContract(query?: {
    study_id?: string;
    site_id?: string;
    country_code?: string;
  }): Promise<ETMFGovernanceContract> {
    try {
      return await this.repository.getGovernanceContract(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch governance contract",
      );
    }
  }

  async getEligibleArtifacts(query: {
    study_id: string;
    site_id?: string;
    country_code?: string;
  }): Promise<{
    items: ETMFGovernedArtifact[];
    governed: boolean;
    model_version_id?: string;
  }> {
    try {
      return await this.repository.getEligibleArtifacts(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch eligible artifacts",
      );
    }
  }

  async getGovernanceCatalog(query?: {
    model_id?: string;
    version_id?: string;
  }): Promise<ETMFGovernanceCatalog> {
    try {
      return await this.repository.getGovernanceCatalog(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to fetch governance catalog",
      );
    }
  }

  async listGovernanceModels(): Promise<{ items: ETMFTmfModelDto[] }> {
    try {
      return await this.repository.listGovernanceModels();
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to list governance models",
      );
    }
  }

  async createGovernanceModel(
    body: CreateTmfModelPayload,
  ): Promise<ETMFTmfModelDto> {
    try {
      return await this.repository.createGovernanceModel(body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create governance model",
      );
    }
  }

  async listGovernanceVersions(
    modelId: string,
  ): Promise<{ items: ETMFTmfModelVersionDto[] }> {
    try {
      return await this.repository.listGovernanceVersions(modelId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to list governance versions",
      );
    }
  }

  async createGovernanceVersion(
    modelId: string,
    body: CreateTmfModelVersionPayload,
  ): Promise<ETMFTmfModelVersionDto> {
    try {
      return await this.repository.createGovernanceVersion(modelId, body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create governance version",
      );
    }
  }

  async submitGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto> {
    try {
      return await this.repository.submitGovernanceVersion(versionId, body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to submit governance version",
      );
    }
  }

  async approveGovernanceVersion(
    versionId: string,
    body: GovernanceReasonPayload,
  ): Promise<ETMFTmfModelVersionDto> {
    try {
      assertESignatureCeremonyPayload(body.eSignature, "TMF model approval");
      return await this.repository.approveGovernanceVersion(versionId, body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to approve governance version",
      );
    }
  }

  async previewGovernanceImpact(
    versionId: string,
  ): Promise<ETMFGovernanceImpact> {
    try {
      return await this.repository.previewGovernanceImpact(versionId);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to preview governance impact",
      );
    }
  }

  async assignStudyModel(
    studyId: string,
    body: { model_version_id: string; reason: string; row_version?: number },
  ): Promise<Record<string, unknown>> {
    try {
      return await this.repository.assignStudyModel(studyId, body);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : "Failed to assign study model",
      );
    }
  }

  async listLegacyMappings(query?: {
    study_id?: string;
  }): Promise<{ items: ETMFLegacyMappingDto[] }> {
    try {
      return await this.repository.listLegacyMappings(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to list legacy mappings",
      );
    }
  }

  async upsertLegacyMapping(
    body: UpsertLegacyMappingPayload,
  ): Promise<ETMFLegacyMappingDto> {
    try {
      return await this.repository.upsertLegacyMapping(body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to upsert legacy mapping",
      );
    }
  }

  async listChangeRequests(query: {
    study_id: string;
  }): Promise<{ items: ETMFChangeRequestDto[] }> {
    try {
      return await this.repository.listChangeRequests(query);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to list change requests",
      );
    }
  }

  async createChangeRequest(body: {
    study_id: string;
    reason: string;
  }): Promise<ETMFChangeRequestDto> {
    try {
      return await this.repository.createChangeRequest(body);
    } catch (error) {
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to create change request",
      );
    }
  }
}
